import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useFormik } from 'formik';
import { FC, useEffect, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../../assets/logo_completo.png';
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import { menuRoutes } from "../../../router/menu";
import { TutorService } from "../../../services/tutor/tutorService";
import SignAgreementMobile from "./SignAgreementMobile";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SignAgreement: FC = () => {

    // HOOKS

    const navigate = useNavigate();
    const { hash } = useParams<{ hash: string }>();

    // STATES

    const [signatureHash, setSignatureHash] = useState<string | undefined>();
    const [agreementLoaded, setAgreementLoaded] = useState(false);
    const [showSignature, setShowSignature] = useState(false);
    const signatureRef = useRef<SignatureCanvas>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfBlob, setPdfBlob] = useState<any | null>(null);
    const [agreementData, setAgreementData] = useState<any | null>(null);
    const { width } = useWindowDimensions();
    const [isLoading, setIsLoading] = useState(false);
    const [signingAgreement, setSigningAgreement] = useState(false);

    // FUNCTIONS

    const handleObtainAgreementInfo = async (signatureHash: string | undefined) => {
        try {
            // En este caso, no necesitamos obtener información adicional del convenio
            // ya que el hash contiene toda la información necesaria
            setAgreementData({ hash: signatureHash });
        } catch (error) {
            console.error(error);
            navigate(menuRoutes.tutor_agreement.error);
        }
    };

    const handlePreviewAgreement = async (signatureHash: string | undefined) => {
        try {
            if (!signatureHash) {
                throw new Error('Hash no válido');
            }
            const service = await (new TutorService()).previewAgreementDocument(signatureHash);
            const response = service.response;
            if (response?.data) {
                let base64String;
                const fileData = response.data;
                // Verificar si el contenido del blob es diferente antes de actualizar el estado
                if (pdfBlob && pdfBlob.size === fileData.byteLength) return;
                const blob = new Blob([fileData]);
                let reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    base64String = reader.result;
                    setPdfBlob(base64String);
                };
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar el convenio');
            navigate(menuRoutes.tutor_agreement.error);
        } finally {
            setAgreementLoaded(true);
        }
    };

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    };

    function onDocumentLoadError(error: any) {
        navigate(menuRoutes.tutor_agreement.error);
    };

    const handleSignAgreement = async (signatureHash: string) => {
        setIsLoading(true);
        setSigningAgreement(true);

        if (signatureRef.current?.isEmpty()) {
            toast.error('Por favor, realice su firma');
            setIsLoading(false);
            setSigningAgreement(false);
            return;
        }
        const signatureImage = signatureRef.current?.toDataURL(); // Obtiene la firma en formato base64

        try {
            if (!signatureImage) {
                throw new Error('Error al obtener la firma');
            }
            const response = await (new TutorService()).signAgreementDocument(signatureHash, signatureImage);
            if (!response?.getResponse() || response.getResponse()?.status !== 200) {
                toast.error('Error al firmar el convenio');
                return;
            }
            // Redirigir a la página de convenio firmado
            navigate(menuRoutes.tutor_agreement.signed);
        } catch (error) {
            console.error(error);
            toast.error('Error al firmar el convenio');
        } finally {
            setIsLoading(false);
            setSigningAgreement(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { signature: null, },
        validateOnChange: false,
        onSubmit: () => {
            if (signatureHash && signatureHash.length > 4) {
                handleSignAgreement(signatureHash);
            } else {
                toast.error('Hash inválido o nulo');
            }
        },
    });

    // USE EFFECTS

    useEffect(() => {
        const fetchData = async () => {
            if (hash && !agreementData) {
                setSignatureHash(hash);
                await handleObtainAgreementInfo(hash);
                await handlePreviewAgreement(hash);
            }
        };
        fetchData();
    }, [hash, agreementData]);

    // RENDER

    return (
        <>
            <div className={`${width < 1024 ? 'block' : 'hidden'}`}>
                <SignAgreementMobile />
            </div>
            <div className={`${width >= 1024 ? 'block' : 'hidden'}`}>
                {/* Componente principal */}
                <div className="relative min-h-screen">
                    <div className="w-full h-[100vh] grid grid-cols-1 xl:grid-cols-3">
                        {/* Vista del PDF */}
                        <div className="col-span-2 bg-gray-100 hidden lg:block">
                            <div className="flex flex-col justify-center items-center h-full">
                                {pdfBlob && agreementLoaded
                                    ? (
                                        <>
                                            <Document
                                                file={pdfBlob}
                                                onLoadSuccess={onDocumentLoadSuccess}
                                                onLoadError={onDocumentLoadError}
                                                className="flex justify-center items-center"
                                            >
                                                <Page
                                                    pageNumber={pageNumber}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                    width={Math.min(window.innerWidth * 0.5, 600)}
                                                />
                                            </Document>
                                            <div className="flex items-center justify-center mt-4 space-x-4">
                                                <button
                                                    onClick={() => setPageNumber((prevPage) => Math.max(prevPage - 1, 1))}
                                                    disabled={pageNumber <= 1}
                                                    title="Página anterior"
                                                    className={"text-2xl p-2 hover:bg-gray-200 rounded-full"}
                                                >
                                                    <FaArrowLeft />
                                                </button>
                                                <span className="text-lg font-medium">
                                                    Página {pageNumber} de {numPages}
                                                </span>
                                                <button
                                                    onClick={() => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1))}
                                                    disabled={pageNumber >= (numPages || 1)}
                                                    title="Página siguiente"
                                                    className={"text-2xl p-2 hover:bg-gray-200 rounded-full"}
                                                >
                                                    <FaArrowRight />
                                                </button>
                                            </div>
                                        </>
                                    )
                                    : <p className="text-gray-500">Cargando convenio...</p>
                                }
                            </div>
                        </div>

                        {/* Formulario de firma */}
                        <div className="mx-auto w-full max-w-md px-4 py-9 flex flex-col justify-center">
                            <div className="text-center mb-5">
                                <img src={fullLogo} className="h-10 mx-auto" alt="Logo de Attiva" />
                                <h1 className="text-2xl font-bold mt-4">Firma de convenio</h1>
                                <p className="text-gray-600 mt-2">
                                    Por favor, revise el convenio y proceda con la firma digital
                                </p>
                            </div>

                            {!showSignature && !signingAgreement && (
                                <Button
                                    type="button"
                                    onClick={() => setShowSignature(true)}
                                    className="w-full mt-10"
                                    size="lg"
                                >
                                    Firmar Convenio
                                </Button>
                            )}

                            {showSignature && !signingAgreement && (
                                <form onSubmit={formik.handleSubmit} className="space-y-5 mt-5">
                                    <div className="border border-gray-300 rounded-md p-2">
                                        <SignatureCanvas
                                            ref={signatureRef}
                                            canvasProps={{
                                                width: 500,
                                                height: 200,
                                                className: 'signature-canvas',
                                            }}
                                        />
                                        <div className="mt-2 flex justify-between">
                                            <Button type="button" onClick={() => signatureRef.current?.clear()} variant="outline">
                                                Limpiar
                                            </Button>
                                            <Button
                                                type="submit"
                                                onClick={() => {
                                                    if (signatureRef.current?.isEmpty()) {
                                                        toast.error('Por favor, realice su firma');
                                                        return;
                                                    }
                                                    const signatureImage = signatureRef.current?.toDataURL();
                                                    formik.setFieldValue('signature', signatureImage);
                                                    handleObtainAgreementInfo(hash);
                                                }}
                                                isLoading={isLoading}
                                                disabled={isLoading}
                                            >
                                                Firmar
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            {signingAgreement && (
                                <div className="flex flex-col justify-center items-center text-center">
                                    <DotLottieReact
                                        src="https://lottie.host/4f0e8e8b-b9b1-4b1b-8b1b-8b1b8b1b8b1b/4f0e8e8b.lottie"
                                        loop
                                        autoplay
                                        className="w-32 h-32"
                                    />
                                    <h2 className="text-xl font-semibold mt-4">Firmando convenio...</h2>
                                    <p className="text-gray-600 mt-2">
                                        Por favor, espere mientras procesamos su firma
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignAgreement;